import { Modal, Box, Flex, Text, Paper, ThemeIcon } from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import type { CollaborationPaymentSuccessData } from "../hooks/useCollaborationEngagementPayment";

type CollaborationPaymentSuccessModalProps = {
  opened: boolean;
  onClose: () => void;
  paymentData: CollaborationPaymentSuccessData | null;
};

export default function CollaborationPaymentSuccessModal({
  opened,
  onClose,
  paymentData,
}: CollaborationPaymentSuccessModalProps) {
  if (!paymentData) return null;

  const bothPartiesPaid = paymentData.newStatus === "BOTH_PAID";
  const acceptedNdaWithPayment = !!paymentData.acceptedNdaWithPayment;
  const bothNdaFullySigned = !!paymentData.bothNdaBecameSigned;
  const creditsCharged =
    typeof paymentData.creditsCharged === "number" ? paymentData.creditsCharged : null;
  const amountLabel =
    creditsCharged === null
      ? "N/A"
      : paymentData.paymentMethod === "Collaboration Credits"
        ? `${creditsCharged} Credits`
        : `$${creditsCharged}`;
  const creditsPendingBothParties =
    !bothPartiesPaid && creditsCharged !== null && creditsCharged === 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} fz="lg">
          Payment Successful
        </Text>
      }
      centered
      radius="md"
      size="lg"
    >
      <Paper
        radius="lg"
        withBorder
        p="lg"
        mb="md"
        style={{
          background: "linear-gradient(to bottom, var(--mantine-color-green-0), white)",
        }}
      >
        <Flex direction="column" align="center" gap="sm">
          <ThemeIcon size={56} radius={56} color="green" variant="light">
            <IconCircleCheck size={32} />
          </ThemeIcon>
          <Text fz="lg" fw={500} ta="center">
            Payment Successful!
          </Text>
          <Text c="slate.5" fz="sm" ta="center">
            {bothPartiesPaid
              ? "Your collaboration workspace is now active"
              : creditsPendingBothParties
                ? "You've completed your step. Credits are debited from both companies only after the other party pays too."
                : "Your payment is complete — waiting for the other party to pay"}
          </Text>
        </Flex>
      </Paper>

      <Paper radius="lg" withBorder p="md" mb="md">
        <Text fw={500} mb="sm">
          Payment Details
        </Text>

        <Flex direction="column" gap="sm">
          <Flex justify="space-between" align="center">
            <Text fz="sm" c="slate.5">
              Transaction ID
            </Text>
            <Text fz="sm" fw={500}>
              {paymentData.transactionId}
            </Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text fz="sm" c="slate.5">
              {creditsPendingBothParties ? "Credits charged" : "Amount Paid"}
            </Text>
            <Text fz="sm" fw={500}>
              {creditsPendingBothParties ? "After both parties pay" : amountLabel}
            </Text>
          </Flex>

          <Flex justify="space-between" align="center">
            <Text fz="sm" c="slate.5">
              Date
            </Text>
            <Text fz="sm" fw={500}>
              {paymentData.date}
            </Text>
          </Flex>
        </Flex>
      </Paper>

      <Paper
        radius="lg"
        p="md"
        mb="md"
        style={{
          background: "#2563EB0D",
          border: "1px solid #2563EB33",
        }}
      >
        <Text fw={500} mb="sm">
          What's Next?
        </Text>

        <Flex direction="column" gap="md">
          <Flex gap="md" align="flex-start">
            <ThemeIcon size={28} radius={28} color="primary">
              <Text fz="xs" fw={500} c="white">
                1
              </Text>
            </ThemeIcon>
            <Box>
              <Text fz="sm" fw={500}>
                Access Your Workspace
              </Text>
              <Text fz="xs" c="slate.5">
                View full company details and start collaborating
              </Text>
            </Box>
          </Flex>

          <Flex gap="md" align="flex-start">
            <ThemeIcon size={28} radius={28} color="primary">
              <Text fz="xs" fw={500} c="white">
                2
              </Text>
            </ThemeIcon>
            <Box>
              <Text fz="sm" fw={500}>
                {bothPartiesPaid && (bothNdaFullySigned || acceptedNdaWithPayment)
                  ? "NDA"
                  : "Sign the NDA"}
              </Text>
              <Text fz="xs" c="slate.5">
                {bothPartiesPaid && bothNdaFullySigned
                  ? "Both parties have signed. You can open the NDA page anytime to view the agreement."
                  : bothPartiesPaid && acceptedNdaWithPayment
                    ? "Your signature was recorded when you paid. If your partner still needs to sign, they will get an email with a link."
                    : bothPartiesPaid
                      ? "Review and accept the auto-generated NDA on the NDA page"
                      : acceptedNdaWithPayment
                        ? "Your acceptance is saved. When both companies have paid, your signature will be applied automatically."
                        : "Available after both parties have completed payment — you will receive an email with a link"}
              </Text>
            </Box>
          </Flex>

          <Flex gap="md" align="flex-start">
            <ThemeIcon size={28} radius={28} color="primary">
              <Text fz="xs" fw={500} c="white">
                3
              </Text>
            </ThemeIcon>
            <Box>
              <Text fz="sm" fw={500}>
                Begin Collaboration
              </Text>
              <Text fz="xs" c="slate.5">
                Share documents and track progress
              </Text>
            </Box>
          </Flex>
        </Flex>
      </Paper>

      <Text ta="center" fz="sm" c="slate.6">
        {bothPartiesPaid && bothNdaFullySigned
          ? "Your collaboration workspace is ready — no further NDA action is required from you."
          : bothPartiesPaid && acceptedNdaWithPayment
            ? "Open the NDA page to read the agreement; your acceptance is already on file."
            : bothPartiesPaid
              ? "Check your email for a link to the NDA page, or open it from your collaboration requests."
              : acceptedNdaWithPayment
                ? "After the other party pays, we will finalize your NDA signature if no further step is needed."
                : "You will receive an email to sign the NDA once both parties have paid"}
      </Text>
    </Modal>
  );
}
